/**
 * Team: the people page, four tabs under one band -- Team Roster ·
 * Availability · Map · SPICE Schedule. The ?tab= values behind them are
 * their own thing (directory / capacity / map / spice_week, unpinned from
 * the visible label or its position in the rail).
 *
 * Route: /t/:tenantSlug/team (registry entry 'team'; the primary-nav pill
 * and the hub's People & Office tile both land here).
 *
 *   ?tab=directory    Team Roster: who is who, with birthdays and work
 *                     anniversaries (the default; every view_staff_tools
 *                     seat)
 *   ?tab=capacity     Availability: the care-manager capacity board and
 *                     week picker
 *   ?tab=map          Map: where everyone is, at full width
 *   ?tab=spice_week   SPICE Schedule: the SPICE weekly grid, therapists
 *                     down, weekdays across, each past entry marked with
 *                     its outcome (every view_staff_tools seat; read-only
 *                     for a SPICE therapist, a second live editor of the
 *                     client record's own schedule for the office)
 *
 * The page gates on canViewStaffTools, the hub floor every internal role
 * holds — Team Roster and SPICE Schedule stay at that floor. Availability
 * and Map are shown only to canViewWorkspace holders, the gate on the
 * capacity read and the tile relay; a hidden tab asked for by URL coerces
 * to Team Roster, and the routes behind it still refuse.
 *
 * Layout: the SectionBand and the tab rail take the wide axis. The Team
 * Roster and Availability bodies share that container; the Map body has
 * no width cap so the map can use the whole viewport.
 */

import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canViewStaffTools, canViewWorkspace } from '@/lib/capabilities';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES, PageShell } from '@/components/primitives/PageShell';
import { SectionBand } from '@/components/primitives/SectionBand';
import { TeamTabs, resolveTeamTab, type TeamTab } from '@/components/team/TeamTabs';
import { DirectoryTab } from '@/components/team/DirectoryTab';
import { SpiceWeekTab } from '@/components/team/SpiceWeekTab';
import { CapacityTab } from '@/components/team/CapacityTab';
import { MapTab } from '@/components/team/MapTab';

export function Team(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const allowed = canViewStaffTools(whoami);
  const hideCapacityTabs = !canViewWorkspace(whoami);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = resolveTeamTab(searchParams.get('tab'), { hideCapacityTabs });

  const setTab = (next: TeamTab): void => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'directory') sp.delete('tab');
    else sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  const wide = `${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER}`;

  return (
    <PageShell
      maxWidth="bleed"
      testId="team-page"
      access={{ allowed, deniedMessage: "The Team page isn't part of your access. Ask {contact_admin} if your work needs it." }}
    >
      <SectionBand
        testId="team-band"
        width="wide"
        eyebrow="Olive · People"
        title="Team"
      />
      <div className={`${wide} pt-4`}>
        <TeamTabs activeTab={tab} hideCapacityTabs={hideCapacityTabs} onChange={setTab} />
      </div>
      {tab === 'map' ? (
        <div id="team-tabpanel" role="tabpanel" aria-labelledby="team-tab-map" data-testid="team-map-body" className={`${PAGE_GUTTER} py-5`}>
          <MapTab tenantSlug={tenantSlug} />
        </div>
      ) : (
        <div id="team-tabpanel" role="tabpanel" aria-labelledby={`team-tab-${tab}`} data-testid="team-body" className={`${wide} py-5`}>
          {tab === 'capacity' ? (
            <CapacityTab tenantSlug={tenantSlug} />
          ) : tab === 'spice_week' ? (
            <SpiceWeekTab tenantSlug={tenantSlug} />
          ) : (
            <DirectoryTab tenantSlug={tenantSlug} />
          )}
        </div>
      )}
    </PageShell>
  );
}
